import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MenuController, Platform } from '@ionic/angular';
import { Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import { auditTime } from 'rxjs/operators';
import { Events } from 'src/app/shared/services/global/events.service';
import { GlobalService, AnalyticsService } from 'src/app/shared/services';
import { EventDataService } from 'src/app/shared/services/eventData/event-data.service';
import { UserDataService } from 'src/app/shared/services/user-data/user-data.service';
import { LogoutService } from 'src/app/shared/services/logout/logout.service';
import { DaoGalleryService } from 'src/app/providers/db/dao-gallery.service';
import { EventColors } from 'src/app/models/event-colors';
import { TypeTracking } from 'src/app/enums/type-tracking';
import { TypeVisionModule } from 'src/app/models/type-vision-module';
import { IGalleryFolder, IGalleryBreadcrumbItem } from 'src/app/models/gallery.models';
import { getGalleryViewMode } from 'src/app/shared/selectors/interactivity.selectors';
import { SetGalleryViewMode } from 'src/app/shared/actions/interactivity.actions';

@Component({
    selector: 'app-gallery',
    templateUrl: './gallery.page.html',
    styleUrls: ['./gallery.page.scss'],
})
export class GalleryPage implements OnInit, OnDestroy {
    private subs: Subscription[] = [];

    eventId: string;
    moduleId: string;

    module: any;
    folders: IGalleryFolder[] = [];
    filteredFolders: IGalleryFolder[] = [];
    loader: boolean = true;
    eventColors: EventColors = new EventColors();
    language: string;
    isMobile: boolean = false;
    backBtn: boolean = true;
    menuBadge: number = 0;

    searchQuery: string = '';
    breadcrumbItems: IGalleryBreadcrumbItem[] = [];

    private currentViewMode: 'grid' | 'list' = 'grid';

    get effectiveViewMode(): 'grid' | 'list' {
        return this.isMobile ? this.currentViewMode : 'grid';
    }

    constructor(
        private route: ActivatedRoute,
        public global: GlobalService,
        private events: Events,
        private menuCtrl: MenuController,
        private SAnalytics: AnalyticsService,
        private SEventData: EventDataService,
        private Slogout: LogoutService,
        private userDataService: UserDataService,
        private daoGallery: DaoGalleryService,
        private store: Store,
        private platform: Platform,
    ) {
        this.menuCtrl.enable(true);

        const params = this.route.snapshot.params;
        this.eventId = params.eventId;
        this.moduleId = params.moduleId;
        this.language = this.SEventData.getLanguage();

        this.menuBadge = this.global.notificationBadge;
        this.events.subscribe('menuBadge', () => {
            this.menuBadge = this.global.notificationBadge;
        });

        this.SAnalytics.moduleAccess(this.eventId, this.moduleId);
        this.SAnalytics.createTrackRoadToModule(this.eventId, this.moduleId, TypeTracking.ACCESS_TO_IMAGES_MODULE);

        const sub = this.store.select(getGalleryViewMode).subscribe(mode => {
            if (mode) this.currentViewMode = mode;
        });
        this.subs.push(sub);
    }

    ngOnInit() {
        if (this.platform.is('ios') || this.platform.is('android')) {
            this.isMobile = true;
        }
        if (this.global.previousPage === 'container') {
            this.backBtn = false;
        } else {
            this.backBtn = true;
        }

        this.loadColors();
        this.loadModule();
    }

    ngOnDestroy(): void {
        this.subs.forEach(s => s.unsubscribe());
    }

    private loadModule(): void {
        const sub = this.daoGallery.getModule(this.moduleId).subscribe((moduleData) => {
            this.module = moduleData;
            const order = moduleData?.['typeOrder'];
            let sortOrder: any = 'desc';
            if (order === 'oldest') sortOrder = 'asc';
            else if (order === 'custom') sortOrder = 'custom';
            else sortOrder = 'desc';

            this.loadFolders(sortOrder);
        });
        this.subs.push(sub);
    }

    private loadFolders(order: any): void {
        const sub = this.daoGallery.getFolders(this.moduleId, order)
            .pipe(auditTime(100))
            .subscribe((folders: any) => {
                this.filterFolders(folders as IGalleryFolder[]);
            });
        this.subs.push(sub);
    }

    private filterFolders(folders: IGalleryFolder[]): void {
        this.userDataService.getUserDataSnapshot(this.eventId).then(async (userData) => {
            this.filteredFolders = [];
            folders.forEach((folder) => {
                if (folder.typeVision === TypeVisionModule.DIVIDED_BY_GROUPS) {
                    const found = Object.keys(folder.groups || {}).findIndex(
                        (groupId) => userData.groups?.[groupId] !== undefined
                    );
                    if (found > -1) {
                        this.filteredFolders.push(folder);
                    }
                } else {
                    this.filteredFolders.push(folder);
                }
            });
            this.folders = this.filteredFolders;
            this.breadcrumbItems = [
                { label: this.module?.name?.[this.language] || 'Gallery', route: '' },
            ];

            for (const folder of this.folders) {
                try {
                    const count = await this.daoGallery.getImageCount(this.moduleId, folder.uid);
                    folder.imageCount = count;
                } catch { }
            }

            this.loader = false;
        });
    }

    private loadColors(): void {
        const sub = this.SEventData.getEventDataListener(this.eventId).subscribe((eventData) => {
            this.eventColors = eventData.colors;
        });
        this.subs.push(sub);
    }

    onSearch(query: string): void {
        this.searchQuery = query;
        if (!query || query.trim() === '') {
            this.folders = this.filteredFolders;
            return;
        }
        const q = query.toLowerCase();
        this.folders = this.filteredFolders.filter((f) => {
            const name = f.name?.[this.language] || '';
            return name.toLowerCase().includes(q);
        });
    }

    onClearSearch(): void {
        this.searchQuery = '';
        this.folders = this.filteredFolders;
    }

    onToggleViewMode(mode: 'grid' | 'list'): void {
        this.store.dispatch(new SetGalleryViewMode({ viewMode: mode }));
    }

    onBreadcrumbNavigate(item: IGalleryBreadcrumbItem): void {
        // no-op on root
    }
}
